import { generatePath } from 'react-router-dom';

import { ProductNames } from '@/components/providers/ApplicantContextProvider/ApplicantContextProvider.types';

import apiInstance from '@/components/shared/api';

import { APIRoutes } from '@/components/shared/routes';
import { Config } from '@/components/shared/utils/config';

export interface NeedsMoreInfoPreviewResponse {
    subject: string;
    from: string;
    to: string;
    cc?: string[];
    bcc?: string[];
    body: string;
}

export interface NeedsMoreInfoRequest {
    applicantId: string;
    products: ProductNames[];
    notes: string;
    docsOnly: boolean;
    isInternal?: boolean;
}

export enum MoreInfoType {
    DocsOnly = 1,
    FullApp = 2,
}

export interface RequestInfoRequest {
    applicantId: string;
    conditionId?: string;
    moreInfoType: MoreInfoType;
    moreInfoMessage: string;
    isInternal: boolean;
    products: ProductNames[];
}

export interface CancelInformationRequestRequest {
    applicantId: string;
    informationRequestId: string;
}

export async function postNeedsMoreInfoPreview({
    applicantId,
    products,
    notes,
    docsOnly,
}: NeedsMoreInfoRequest): Promise<NeedsMoreInfoPreviewResponse> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_NEEDS_MORE_INFO_PREVIEW, {
        applicantId,
    });
    return (
        await apiInstance.post<NeedsMoreInfoPreviewResponse>(endpointUrl, {
            products,
            notes,
            docsOnly,
        })
    ).data;
}

export async function postNeedsMoreInfo({
    applicantId,
    products,
    notes,
    docsOnly,
    isInternal,
}: NeedsMoreInfoRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_NEEDS_MORE_INFO, {
        applicantId,
    });
    return (await apiInstance.post<unknown>(endpointUrl, { products, notes, docsOnly, isInternal }))
        .data;
}

const PRODUCT_NAME_TO_SERVER: Record<ProductNames, string> = {
    [ProductNames.FACTORING]: 'Factoring',
    [ProductNames.LOADPAY]: 'LoadPay',
    [ProductNames.TCSFUEL]: 'TcsFuel',
};

export async function postRequestInfo({
    applicantId,
    conditionId,
    moreInfoType,
    moreInfoMessage,
    isInternal,
    products,
}: RequestInfoRequest): Promise<unknown> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_REQUEST_INFO, {
        applicantId,
    });
    return (
        await apiInstance.post<unknown>(endpointUrl, {
            conditionId,
            moreInfoType: MoreInfoType[moreInfoType],
            moreInfoMessage,
            isInternal,
            products: products.map((p) => PRODUCT_NAME_TO_SERVER[p]),
        })
    ).data;
}

export async function deleteInformationRequest({
    applicantId,
    informationRequestId,
}: CancelInformationRequestRequest): Promise<void> {
    const endpointUrl = generatePath(APIRoutes.APPLICANT_CANCEL_REQUEST_INFO, {
        applicantId,
        informationRequestId,
    });
    await apiInstance.delete(endpointUrl);
}

// Server serializes ProductNames as its C# member name, not the portal's ProductNames values.
export type ServerProductName = 'Unknown' | 'Factoring' | 'LoadPay' | 'TcsFuel';

export interface RecentNeedsMoreInfoSentRow {
    id: string;
    applicantId: string;
    companyName: string;
    notificationId: string | null;
    products: ServerProductName[];
    moreInfoType: keyof typeof MoreInfoType | null;
    moreInfoMessage: string;
    author: string | null;
    isInternal: boolean;
    isActive: boolean;
    isPendingApplicantResponse: boolean;
    dateCreated: string;
    dateModified: string;
}

export async function getRecentNeedsMoreInfoSent(): Promise<RecentNeedsMoreInfoSentRow[]> {
    return (
        await apiInstance.get<RecentNeedsMoreInfoSentRow[]>(APIRoutes.RECENT_NEEDS_MORE_INFO, {
            baseURL: `${Config.BaseApiUrl}/originations-review-portal`,
        })
    ).data;
}
